/*
 * <media-lightbox>
 * Full-screen image zoom dialog inside a native `<dialog class="modal">` (see snippets/media-lightbox.liquid),
 * modelled on Horizon's zoom dialog. Closing on Escape, focus return and the scroll lock come from the
 * dialog and modal-dialog.js. This element:
 *   - opens on the image named by a trigger's `data-lightbox-media-id`
 *     (`data-modal-open="{dialog id}"` triggers, delegated so they can live anywhere on the page)
 *   - closes on a click on an image
 *   - keeps the thumbnail rail in sync with the image in view; a thumbnail scrolls to its image
 *
 * Public API:
 *   element.open(indexOrMediaId)
 *   element.goTo(indexOrMediaId, { instant })
 */
if (!customElements.get("media-lightbox")) {
  class MediaLightbox extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector("dialog");
      this.list = this.dialog?.querySelector("[data-lightbox-list]");
      if (!this.list) return;

      this.items = [...this.list.querySelectorAll("[data-lightbox-item]")];
      this.rail = this.dialog.querySelector("[data-lightbox-thumbnails]");
      this.thumbnails = [...this.dialog.querySelectorAll("[data-lightbox-thumbnail]")];
      this.currentIndex = 0;
      this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      document.addEventListener("click", this.onTriggerClick.bind(this), { signal });
      this.list.addEventListener("click", this.onListClick.bind(this), { signal });
      this.rail?.addEventListener("click", this.onThumbnailClick.bind(this), { signal });
      if (this.rail)
        this.list.addEventListener("scroll", this.onScroll.bind(this), { passive: true, signal });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      cancelAnimationFrame(this.scrollFrame);
      clearTimeout(this.unlockTimer);
    }

    get behavior() {
      return this.reducedMotion.matches ? "instant" : "smooth";
    }

    /**
     * modal-dialog.js may open the dialog before or after this runs, so opening is idempotent.
     */
    onTriggerClick(event) {
      const trigger = event.target.closest(`[data-modal-open="${CSS.escape(this.dialog.id)}"]`);
      if (!trigger) return;

      event.preventDefault();
      this.open(trigger.dataset.lightboxMediaId ?? 0);
    }

    open(target = 0) {
      if (!this.dialog.open) this.dialog.showModal();
      this.goTo(target, { instant: true });
    }

    /**
     * Scrolls to an image by its index (number) or Shopify media id (string).
     * Unknown media ids fall back to the first image.
     */
    goTo(target, { instant = false } = {}) {
      const index =
        typeof target === "number"
          ? target
          : Math.max(
              0,
              this.items.findIndex((item) => item.dataset.mediaId === String(target)),
            );
      const item = this.items[index];
      if (!item) return;

      // The scroll it starts doesn't move the rail; it may stop short of the image near the end
      this.lockScrollTracking();
      this.list.scrollTo({ top: item.offsetTop, behavior: instant ? "instant" : this.behavior });
      this.setCurrent(index, { instant });
    }

    lockScrollTracking() {
      clearTimeout(this.unlockTimer);
      this.scrollLocked = true;

      const unlock = () => {
        clearTimeout(this.unlockTimer);
        this.list.removeEventListener("scrollend", unlock);
        this.scrollLocked = false;
      };
      this.list.addEventListener("scrollend", unlock, { once: true });
      // No scrollend when the list is already in place
      this.unlockTimer = setTimeout(unlock, 1000);
    }

    onListClick(event) {
      if (event.target.closest("[data-lightbox-item]")) this.dialog.close();
    }

    onThumbnailClick(event) {
      const thumbnail = event.target.closest("[data-lightbox-thumbnail]");
      if (thumbnail) this.goTo(this.thumbnails.indexOf(thumbnail));
    }

    /**
     * The image in view is the one under a point that travels from the top of the screen to the
     * bottom as the list scrolls, so the first and last images are reached even when several fit
     * on screen.
     */
    onScroll() {
      if (this.scrollLocked) return;

      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => {
        const { scrollTop, scrollHeight, clientHeight } = this.list;
        const progress = scrollHeight > clientHeight ? scrollTop / (scrollHeight - clientHeight) : 0;
        const point = scrollTop + clientHeight * progress;
        const index = this.items.findLastIndex((item) => item.offsetTop <= point);
        this.setCurrent(Math.max(0, index));
      });
    }

    setCurrent(index, { instant = false } = {}) {
      if (index === this.currentIndex && !instant) return;
      this.currentIndex = index;

      const thumbnail = this.thumbnails[index];
      if (!thumbnail) return;

      this.thumbnails.forEach((button, i) => button.setAttribute("aria-current", i === index));

      // Centres the thumbnail in the rail, whichever way the rail runs
      this.rail.scrollTo({
        top: thumbnail.offsetTop - (this.rail.clientHeight - thumbnail.offsetHeight) / 2,
        left: thumbnail.offsetLeft - (this.rail.clientWidth - thumbnail.offsetWidth) / 2,
        behavior: instant ? "instant" : this.behavior,
      });
    }
  }

  customElements.define("media-lightbox", MediaLightbox);
}
